import { Platform } from '@/types/platform';
import { StandardResponse } from '@/types/response';
import { BlindReviewLog } from '@/types/review';
import { buildQuery } from '@/utils/buildQuery';

import AppApi from './app-api';

/**
 * 블라인드 로그 조회 쿼리 타입
 */
export type GetBlindReviewLogsQuery = {
  user_id?: string;
  review_id?: string;
  shop_id?: string;
  log_id?: number;
};

/**
 * 수정 로그 생성 요청 타입
 */
export type CreateModifyReasonLogDto = {
  log_id: number;
  updated_reason: string;
};

/**
 * 수정 로그 조회 쿼리 타입
 */
export type GetModifyReasonLogsQuery = {
  user_id?: string;
  log_id?: number;
  page?: number;
  limit?: number;
};

/**
 * 블라인드 리뷰 로그 상세 응답 타입 (GET /blind-review 응답)
 * - user, shop, review relation 포함
 */
export type BlindReviewLogDetail = {
  log_id: number;
  user_id: string;
  shop_id: string;
  review_id: string;
  status: string;
  refresh_count: number;
  reason: string | null;
  created_at: string;
  updated_at: string;
  user: {
    business_number: string;
  };
  shop: {
    shop_id: string;
    name: string;
    platform: Platform;
  };
  review: {
    review_id: string;
    created_at: string;
    reviewer: string;
    content: string;
  };
};

/**
 * 블라인드 리뷰 API
 * - GET /blind-review: 블라인드 신청 내역 조회
 * - POST /blind-review/review/:review_id: 블라인드 신청
 * - POST /blind-review/review/:review_id/update-reason: AI 사유 갱신
 */
export class BlindReviewApi {
  constructor(private api: AppApi) {}

  /**
   * 블라인드 신청 내역 조회 (배열 반환)
   */
  async getBlindReviewLogs(
    query?: GetBlindReviewLogsQuery,
  ): Promise<StandardResponse<BlindReviewLogDetail[]>> {
    const queryString = buildQuery({
      user_id: query?.user_id,
      review_id: query?.review_id,
      shop_id: query?.shop_id,
      log_id: query?.log_id,
    });

    const res = await this.api.INSTANCE.get(`/blind-review${queryString}`);
    return res.data;
  }

  /**
   * 블라인드 신청
   */
  async requestBlindReview(reviewId: string): Promise<StandardResponse<BlindReviewLog>> {
    const res = await this.api.INSTANCE.post(`/blind-review/review/${reviewId}`);
    return res.data;
  }

  /**
   * AI 사유 갱신 (비동기)
   */
  async refreshReason(reviewId: string): Promise<StandardResponse<BlindReviewLog>> {
    const res = await this.api.INSTANCE.post(`/blind-review/review/${reviewId}/update-reason`);
    return res.data;
  }

  /**
   * 블라인드 신청 내역 삭제 (개발용)
   */
  async deleteBlindReviewLog(reviewId: string): Promise<StandardResponse<unknown>> {
    const res = await this.api.INSTANCE.post(`/blind-review/delete?review_id=${reviewId}`);
    return res.data;
  }

  /**
   * 블라인드 신청 내역 수정 로그 생성
   */
  async createModifyLog(body: CreateModifyReasonLogDto): Promise<StandardResponse<unknown>> {
    const res = await this.api.INSTANCE.post('/blind-review/log', body);
    return res.data;
  }

  /**
   * 블라인드 신청 내역 수정 로그 조회
   */
  async getModifyLogs(query?: GetModifyReasonLogsQuery): Promise<StandardResponse<unknown>> {
    const queryString = buildQuery({
      user_id: query?.user_id,
      log_id: query?.log_id,
      page: query?.page,
      limit: query?.limit,
    });

    const res = await this.api.INSTANCE.get(`/blind-review/log${queryString}`);
    return res.data;
  }
}

export default BlindReviewApi;
